import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Trophy, Clock } from 'lucide-react';
import './MatchGroupDetails.css';

function MatchGroupDetails({ token, matchGroup, nightMode, onBack }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (matchGroup) {
      fetchGroupMatches();
    }
  }, [matchGroup]);

  const fetchGroupMatches = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/match-groups/${matchGroup._id}/matches`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatches(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching group matches:', error);
      setError('Failed to fetch match details');
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getSeriesScore = () => {
    let teamAWins = 0;
    let teamBWins = 0;
    
    matches.forEach(match => {
      if (match.scoreA > match.scoreB) {
        teamAWins++;
      } else if (match.scoreB > match.scoreA) {
        teamBWins++;
      }
    });
    
    return { teamA: teamAWins, teamB: teamBWins };
  };

  const getSeriesStatus = () => {
    const score = getSeriesScore();
    const winsNeeded = Math.ceil(matchGroup.bestOf / 2);
    
    if (score.teamA >= winsNeeded) return 'teamA';
    if (score.teamB >= winsNeeded) return 'teamB';
    return 'ongoing';
  };

  if (loading) {
    return (
      <div className={`match-group-details ${nightMode ? 'night-mode' : ''}`}>
        <div className="loading">Loading match details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`match-group-details ${nightMode ? 'night-mode' : ''}`}>
        <div className="details-header">
          <button onClick={onBack} className="back-btn">
            <ArrowLeft size={16} /> Back to Match Groups
          </button>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const seriesScore = getSeriesScore();
  const status = getSeriesStatus();
  const winsNeeded = Math.ceil(matchGroup.bestOf / 2);

  return (
    <div className={`match-group-details ${nightMode ? 'night-mode' : ''}`}>
      <div className="details-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft size={16} />
          <span>Series</span>
        </button>
        <h1 title={matchGroup.name}>{matchGroup.name}</h1>
      </div>

      <div className="series-overview">
        <div className="series-info-card">
          <div className="series-title">
            {matchGroup.organization && (
              <span className="series-org">{matchGroup.organization}</span>
            )}
            <span className={`type-badge ${matchGroup.type.toLowerCase()}`}>
              {matchGroup.type}
            </span>
          </div>
          
          <div className="series-board">
            <div className={`series-row ${status === 'teamA' ? 'is-winner' : ''}`}>
              <span className="series-team-name" title={matchGroup.teamA}>{matchGroup.teamA}</span>
              <div className="series-pips" aria-hidden="true">
                {Array.from({ length: winsNeeded }).map((_, i) => (
                  <span key={i} className={`pip ${i < seriesScore.teamA ? 'on' : ''}`} />
                ))}
              </div>
              <span className="series-set-score">{seriesScore.teamA}</span>
            </div>
            <div className={`series-row ${status === 'teamB' ? 'is-winner' : ''}`}>
              <span className="series-team-name" title={matchGroup.teamB}>{matchGroup.teamB}</span>
              <div className="series-pips" aria-hidden="true">
                {Array.from({ length: winsNeeded }).map((_, i) => (
                  <span key={i} className={`pip ${i < seriesScore.teamB ? 'on' : ''}`} />
                ))}
              </div>
              <span className="series-set-score">{seriesScore.teamB}</span>
            </div>
          </div>
          
          <div className="series-meta">
            <span>Best of {matchGroup.bestOf}</span>
            <span>·</span>
            <span>{matches.length} sets</span>
            <span>·</span>
            <span>{new Date(matchGroup.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
          </div>
          
          {status !== 'ongoing' && (
            <div className="series-complete">
              <Trophy size={14} />
              Final — {status === 'teamA' ? matchGroup.teamA : matchGroup.teamB}
            </div>
          )}
          
          {matchGroup.note && (
            <p className="series-note">{matchGroup.note}</p>
          )}
        </div>
      </div>

      <div className="sets-section">
        <h3>Sets</h3>
        {matches.length === 0 ? (
          <div className="empty-sets">
            <Trophy size={48} />
            <p>No sets have been played yet in this series.</p>
          </div>
        ) : (
          <div className="sets-list">
            {matches.map((match, index) => (
              <div key={match._id} className="set-card">
                <div className="set-header">
                  <h4>Set {index + 1}</h4>
                  <div className="set-date">
                    {new Date(match.date).toLocaleDateString()} at{' '}
                    {new Date(match.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                
                <div className="set-score">
                  <div className={`set-row ${match.scoreA > match.scoreB ? 'is-winner' : ''}`}>
                    <span className="series-team-name" title={match.teamA}>{match.teamA}</span>
                    <span className="set-points">{match.scoreA}</span>
                  </div>
                  <div className={`set-row ${match.scoreB > match.scoreA ? 'is-winner' : ''}`}>
                    <span className="series-team-name" title={match.teamB}>{match.teamB}</span>
                    <span className="set-points">{match.scoreB}</span>
                  </div>
                </div>
                
                <div className="set-details">
                  <div className="detail-item">
                    <Clock size={14} />
                    <span>Duration: {formatTime(match.time)}</span>
                  </div>
                  {match.note && (
                    <div className="detail-item">
                      <span>Note: {match.note}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MatchGroupDetails;
